/* eslint-disable react/prop-types */
import React, { ChangeEventHandler, useState, useMemo, FC } from 'react'
// Types
import type {
  GetStaticProps,
  GetStaticPaths,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/dist/client/router'

import Link from 'next/link'
import Head from 'next/head'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IoOptionsOutline } from 'react-icons/io5'
import { NetworkStatus } from '@apollo/client'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import { debounce } from 'lodash'
import { ProductFilterKeys } from '../../../types/product'
import Product from '../../components/Product'
import Filter, { FilterEventHandler } from '../../components/Filter'
import Container from '../../components/Container'
import Loading from '../../components/Loading'
import { CollectionSkeleton } from '../../components/skeleton'
import BackTop from '../../components/BackTop'

import { useGetCollectionsByHandler } from '../../hooks/useCollections'
import { CollectionPageSensor } from '../../utils/bloomchicdata/page/collectionpage'
import { CollectionSortList } from '../../utils/constants'
import { FilterObject } from '../../utils/constants/filters'
import { combineFilters, parseQueryToFilter } from '../../utils/query-parser'
import collectionHandlerQuery from '../../api/graphql/collectionHandlerQuery.gql'
import shopQuery from '../../api/graphql/shopQuery.gql'
import initializeApollo from '../../api/client'
import {
  CollectionByHandlerQuery,
  CollectionByHandlerQueryVariables,
  ShopQueryQuery,
} from '../../../types/storefront'
import { useIsomorphicLayoutEffect } from '../../utils/helpers'

const collectionPageSensorMod = () =>
  import('../../utils/bloomchicdata/page/collectionpage')

const COLLECTION_PATH = '/collections/[...handle]'

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: true,
})
/**
 * Get collection info in ssr mode
 */
// @ts-ignore
export const getStaticProps: GetStaticProps<{
  key: string
  collection: CollectionByHandlerQuery['collection']
  shop: ShopQueryQuery['shop']
}> = async (context) => {
  const { handle } = context.params as any
  const [collection] = handle as string[]
  if (!collection) {
    return { props: { data: {} } }
  }
  const client = initializeApollo()
  const collectionQuery = client.query<
    CollectionByHandlerQuery,
    CollectionByHandlerQueryVariables
  >({
    query: collectionHandlerQuery,
    variables: {
      handle: collection as string,
      first: 24,
    },
  })
  const shopQuerys = client.query<ShopQueryQuery>({ query: shopQuery })
  const [cData, shop] = await Promise.all([collectionQuery, shopQuerys])
  /* eslint-disable */
  const collections = cloneDeep(cData.data.collection)
  collections?.products.edges.forEach((p) => {
    if (p.node.metafield?.value) {
      p.node.metafield.value = Buffer.from(p.node.metafield.value).toString(
        'base64'
      )
    }
  })
  /* eslint-disable */
  return {
    revalidate: 60 * 60 * 24,
    props: {
      key: cData.data.collection?.handle,
      // collection: { ...collections },
      collection: collections,
      shop: shop.data.shop,
    },
  }
}

let collectionPageSensor: CollectionPageSensor | null
const debounceReport = debounce(() => collectionPageSensorMod().then((mod) => {
    collectionPageSensor = new mod.CollectionPageSensor()
    collectionPageSensor?.Action()
  }), 300
)

const CollectionPage: FC<InferGetStaticPropsType<typeof getStaticProps>> = (
  props
) => {
  const { shop, collection } = props
  const { title, description } = collection || {}
  const fbImage = get(
    props,
    'products.edges[0].node.images.edges[0].node.transformedSrc'
  )
  const router = useRouter()
  const [filterVisible, setFilterVisible] = useState(false)
  const [onStatus, setOnStatus] = useState<boolean>(false)
  const [filterSelected, setFilters] = useState<FilterObject[]>([])

  const {
    query: { loading, networkStatus },
    sortItem,
    filtersMap,
    variables,
    fetchMore,
    data: { products, collection: data },
    collectionCount,
    dispatchInitialQuery,
  } = useGetCollectionsByHandler(collection)

  /**
   * Sort select action
   */
  const handleSortSelect: ChangeEventHandler<HTMLSelectElement> = (e) => {
    router.push(
      {
        pathname: COLLECTION_PATH,
        query: {
          ...router.query,
          sort_by: e.target.value,
        },
      },
      undefined,
      { shallow: true }
    )
    setOnStatus(false)
    if (e.target.value === 'manual') {
      dispatchInitialQuery(true)
    }
    collectionPageSensor?.SortMethodClick(e.target.value)
  }

  /**
   * Filter action
   */
  const handleFilterSelect: FilterEventHandler = (value) => {
    const querys = combineFilters(value)

    const temp = { ...router.query }
    Object.keys(temp).forEach((key) => {
      if (key !== 'handle' && key !== 'sort_by' && !querys[key]) {
        delete temp[key]
      }
    })

    router.push(
      {
        pathname: COLLECTION_PATH,
        query: {
          ...temp,
          ...querys,
        },
      },
      undefined,
      { shallow: true }
    )

    if (
      !Object.keys(querys).length &&
      (!router.query.sort_by || router.query.sort_by === 'manual')
    ) {
      dispatchInitialQuery(true)
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (process.browser && data && !router.isFallback) {
      debounceReport()
    }
  }, [data])

  const productCountVisible = useMemo(
    () => !router.isFallback && !loading && products?.edges.length,
    [loading, router, products]
  )

  const Seo = () => (
    <Head>
      <title>{title || data?.title} - BloomChic</title>
      {(description || shop?.description || data?.description) && (
        <meta
          name="description"
          content={description || shop?.description || data?.description}
        />
      )}
      {fbImage && (
        <>
          <meta property="og:image" content={fbImage} />
          <meta property="og:image:secure_url" content={fbImage} />
          <meta property="og:image:width" content="436" />
          <meta property="og:image:height" content="160" />
        </>
      )}
    </Head>
  )

  return (
    <>
      {!router.isFallback && <Seo />}
      <Container
        id="collection"
        data-id={data?.id}
        data-id2={collectionPageSensor?.getCollectionID2(data?.id ?? '')}
        data-name={data?.title}
        className="collection md:pb-28"
        key={data?.id}
      >
        <div
          className={`py-3 flex items-center text-sm sm:text-base -mx-2${
            loading && ' opacity-40'
          }
          ${
            !products ||
            loading ||
            [NetworkStatus.refetch, NetworkStatus.setVariables].includes(
              networkStatus
            )
              ? ' pointer-events-none'
              : ''
          }`}
        >
          <div className="flex-1 px-2">
            <div
              className={`py-3 px-5 cursor-pointer flex sm:inline-flex items-center ${
                filterSelected.length > 0 ? 'bg-black text-white' : ''
              }`}
              onClick={() => setFilterVisible(true)}
            >
              <IoOptionsOutline className="mr-2" size={18} />
              <div>
                <span
                  className={`${
                    filterSelected.length > 0 ? 'text-white' : 'text-black'
                  }`}
                >
                  Filter&nbsp;
                </span>
                {filterSelected.length > 0 && (
                  <span>({filterSelected.length})</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 text-center text-sm hidden sm:block relative">
            {productCountVisible ? (
              `${collectionCount} product${collectionCount > 1 ? 's' : ''}`
            ) : (
              // <Spin>&nbsp;</Spin>
              <Loading size={24} />
            )}
          </div>

          <div className="flex-1 text-right px-2 relative">
            <span
              className={`absolute right-1.5 top-1/2 transform -translate-y-1/2 z-10 transition-transform ${
                onStatus ? 'rotate-180' : ''
              }`}
            >
              <MdOutlineKeyboardArrowDown size={20} />
            </span>
            <select
              value={sortItem?.searchValue}
              className="py-3 px-5 text-black font-medium"
              name="sortlist"
              onChange={handleSortSelect}
              onClick={() => setOnStatus(true)}
              onMouseLeave={() => setOnStatus(false)}
            >
              {CollectionSortList.map((i) => (
                <option value={i.searchValue} key={i.label}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 text-center text-xs sm:hidden mb-4 relative">
          {productCountVisible ? (
            `${collectionCount} product${collectionCount > 1 ? 's' : ''}`
          ) : (
            // <Spin>&nbsp;</Spin>
            <Loading size={24} />
          )}
        </div>

        {filtersMap && (
          <Filter
            open={filterVisible}
            filters={filtersMap as any}
            defaultSelect={parseQueryToFilter(
              router.query,
              filtersMap.size?.values.map((i) => ({
                ...i,
                type: ProductFilterKeys.size,
              })) as any,
              filtersMap.product_type?.values.map((i) => ({
                ...i,
                type: ProductFilterKeys.type,
              })) as any
            )}
            onOpenChange={(open) => setFilterVisible(open)}
            onSelect={handleFilterSelect}
            onChange={(filters) => setFilters(filters)}
          />
        )}

        {!products ||
        (!products?.edges.length && loading) ||
        [NetworkStatus.refetch, NetworkStatus.setVariables].includes(
          networkStatus
        ) ? (
          <CollectionSkeleton />
        ) : (
          <InfiniteScroll
            className="grid grid-cols-2 gap-x-3 md:gap-x-5 md:grid-cols-4 pb-6"
            next={() => {
              fetchMore()
              collectionPageSensor?.CollectionTurnPage(1)
            }}
            hasMore={Boolean(products?.pageInfo.hasNextPage)}
            loader={
              <Loading
                className="w-full flex-1 col-span-2 flex-shrink-0 mt-3 pb-5 md:col-span-4 md:mt-10"
                size={35}
              />
            }
            dataLength={products?.edges.length || (variables.first as number)}
          >
            {products?.edges.map((i, index) => (
              <Product
                className="mb-6"
                collectionId={data?.id ?? ''}
                collectionTitle={data?.title ?? ''}
                data={i.node}
                key={i.node.id}
                productIndex={index + 1}
              />
            ))}
            <BackTop />
          </InfiniteScroll>
        )}

        {/* No more */}
        {!loading && data && !products?.pageInfo.hasNextPage && (
          <div>
            <div className="relative text-center text-sm text-gray-400">
              <span className="absolute w-full border-b left-0 top-1/2" />
              <span className="inline-block px-6 bg-white z-10 relative">
                No more products
              </span>
            </div>
            <Link href="/" passHref prefetch={false}>
              <a
                className="underline text-center block py-8 text-xs sm:text-base"
                style={{ color: '#2f92eb' }}
              >
                Back to homepage
              </a>
            </Link>
          </div>
        )}
      </Container>
    </>
  )
}

export default CollectionPage
